import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { addPharmacyOptions, getPharmacyOptions, editPharmacyOptions } from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { BuildingIcon } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { FormProps } from '~/shared/forms/Form';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	name: Yup.string().label('Pharmacy Name').nullable().required(),
	address: Yup.object().label('Address').address().required(),
	phoneNumber: Yup.string().label('Phone Number').nullable().required().phoneNumber(),
	faxNumber: Yup.string().label('Fax Number').nullable().phoneNumber(),
	emailAddress: Yup.string().label('Email Address').nullable().email(),
	pharmacyHours: Yup.string().label('Pharmacy Hours').nullable(),
	notes: Yup.string().label('Notes').nullable()
});

export function EditPharmacyForm() {
	const { id } = useParams<'id'>();
	const options = getPharmacyOptions(id!);
	const { data: pharmacy } = useSuspenseQuery(options);
	return (
		<PharmacyForm
			initialValues={{ ...pharmacy, pharmacyId: id }}
			saveMutation={{
				...editPharmacyOptions,
				invalidateQueryKeys: [options.queryKey]
			}}
			title={`Edit Pharmacy: ${pharmacy?.name}`}
			description={`Make changes to the pharmacy ${pharmacy?.name}`}
		/>
	);
}

export function AddPharmacyForm() {
	const navigate = useNavigate();
	return (
		<PharmacyForm
			initialValues={{
				name: null,
				address: null,
				phoneNumber: null,
				faxNumber: null,
				emailAddress: null,
				pharmacyHours: null,
				notes: null
			}}
			saveMutation={{
				...addPharmacyOptions,
				invalidateQueryKeys: [getSearchQueryKey('pharmacy')]
			}}
			onSuccess={() => navigate('/settings/libraries/pharmacies')}
			title="Add Pharmacy"
			description="Add a pharmacy to the system"
		/>
	);
}

function PharmacyForm(props: Pick<FormProps, 'initialValues' | 'saveMutation' | 'onSuccess'> & { title: string; description: string }) {
	const navigate = useNavigate();
	return (
		<LibraryForm
			{...props}
			TitleIcon={BuildingIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to Pharmacy Library',
				errorAction: () => navigate('/settings/libraries/pharmacies')
			}}
		>
			<div className="grid grid-cols-2 gap-6">
				<TextFormField
					name="name"
					inputProps={{
						placeholder: 'Enter Pharmacy Name'
					}}
					className="col-span-2 w-full"
				/>
				<PhoneNumberFormField name="phoneNumber" />
				<PhoneNumberFormField name="faxNumber" />

				<TextFormField name="emailAddress" className="col-span-2" />
				<TextFormField name="pharmacyHours" className="col-span-2" />
				<TextAreaFormField name="notes" className="col-span-2" />
				<div className="col-span-2">
					<AddressFormField name="address" />
				</div>
			</div>
		</LibraryForm>
	);
}
